import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import MonthNavigator from '../components/MonthNavigator'
import { useVoiceInput } from '../hooks/useVoiceInput'

function currentMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function FormulaPage() {
  const [month, setMonth] = useState(currentMonth())
  const [content, setContent] = useState('')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const { transcript, isListening, error: voiceError, supported, start, stop } = useVoiceInput()

  const excelRows = useMemo(() => {
    return content
      .split(/\r?\n/)
      .map((line) => line.trimEnd())
      .filter((line) => line.length > 0)
  }, [content])

  useEffect(() => {
    let alive = true
    setLoading(true)
    api(`/formulas?month=${month}`)
      .then((d) => alive && setContent(d.formula_content || ''))
      .catch(() => alive && setContent(''))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [month])

  const save = async () => {
    setError('')
    setSaved(false)
    try {
      await api('/formulas', { method: 'POST', body: { month, formula_content: content } })
      setSaved(true)
    } catch (e) {
      setError(e.message)
    }
  }

  const toggleVoice = () => {
    if (isListening) {
      stop()
      const spoken = transcript.trim()
      if (spoken) {
        setContent((prev) => {
          const base = prev.trimEnd()
          return base ? `${base}\n${spoken}` : spoken
        })
      }
      return
    }
    start()
  }

  return (
    <div className="flex min-h-[85vh] flex-col gap-3">
      <h1 className="text-3xl font-bold text-white">🧪 藥水配方</h1>
      <MonthNavigator value={month} onChange={setMonth} />

      <textarea
        value={content}
        onChange={(e) => {
          setContent(e.target.value)
          setSaved(false)
        }}
        placeholder="把這個月的藥水配方用講的或打字的記在這裡，回診時給醫師看…"
        className="h-[45vh] w-full flex-1 rounded-2xl border-4 border-white bg-white p-4 text-2xl leading-relaxed text-navy-900 focus:outline-none"
      />

      {isListening && (
        <p className="rounded-xl bg-warn p-2 text-center text-2xl font-bold text-white">
          🎙️ 正在聽你講…{transcript && `「${transcript}」`}
        </p>
      )}
      {voiceError && (
        <p className="rounded-xl bg-red-100 p-2 text-center text-xl font-bold text-warn">⚠️ {voiceError}</p>
      )}

      <button
        type="button"
        disabled={!supported}
        onClick={toggleVoice}
        className={`big-btn text-white ${isListening ? 'bg-warn' : 'bg-safe'} disabled:bg-gray-500`}
      >
        {supported
          ? isListening
            ? '🎙️ 再按一下就停'
            : '🎙️ 按一下開始說話'
          : '這個瀏覽器不支援語音，請直接打字'}
      </button>

      <p className="text-center text-lg text-gray-300">可以一段一段講，停下來後會自動接到下一行</p>

      <div className="rounded-2xl border-4 border-white bg-white/10 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">📄 Excel 預覽</h2>
          <span className="rounded-full bg-navy-700 px-3 py-1 text-lg font-bold text-white">
            {excelRows.length} 列
          </span>
        </div>
        <div className="max-h-[30vh] overflow-auto rounded-xl border-2 border-white bg-white">
          <table className="w-full border-collapse text-left text-xl">
            <thead className="sticky top-0 bg-navy-700 text-white">
              <tr>
                <th className="w-20 border-r-2 border-white px-3 py-2">列</th>
                <th className="px-3 py-2">內容</th>
              </tr>
            </thead>
            <tbody>
              {excelRows.length ? (
                excelRows.map((row, index) => (
                  <tr key={`${index}-${row}`} className={index % 2 === 0 ? 'bg-sky-50' : 'bg-white'}>
                    <td className="border-r border-gray-300 px-3 py-2 font-bold text-navy-900">
                      {index + 1}
                    </td>
                    <td className="whitespace-pre-wrap px-3 py-2 text-navy-900">{row}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className="px-3 py-6 text-center text-xl text-gray-500">
                    還沒有內容，輸入或說話後會顯示在這裡
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error && <p className="text-center text-xl font-bold text-red-300">⚠️ {error}</p>}
      {saved && (
        <p className="rounded-xl bg-green-100 p-2 text-center text-2xl font-bold text-safe">
          ✅ {month} 配方已經存好了！
        </p>
      )}

      {!loading && (
        <button type="button" onClick={save} className="big-btn bg-navy-700 text-white">
          💾 存起這個月的配方
        </button>
      )}
    </div>
  )
}
